<template>
  <div id="app" :class="{ 'nav-collapsed': navCollapsed }">
    <nav-menu v-if="showNavigation" />

    <main :class="{ 'auth': !showNavigation }">
      <nav-header v-if="showNavigation" />

      <section id="content">
        <div
          v-if="navOpen || notificationsOpen"
          class="mask"
          @click="handleOutsideClick"
        />

        <panel-view>
          <router-view />

          <template #panel>
            <side-panel-handler v-if="showSidePanel" />
          </template>
        </panel-view>
      </section>
    </main>

    <notifications-panel v-if="showNavigation" />
    <keyboard-shortcuts />

    <portal-target name="fullscreen" />
    <portal-target name="feedback" />
    <portal-target name="cardfeedback" />
    <portal-target name="modal" />

    <admin-edit-card-modal />
    <admin-edit-post-modal />
    <collection-edit-card-modal />
    <watchlist-alerts-modal />
    <pending-sales-item-edit-modal />
    <add-sale-side-modal />
    <sales-history-item-edit-modal />
    <showcase-share-modal />
  </div>
</template>

<script>
import { mapState } from 'vuex';

import NavMenu from '@/components/NavMenu';
import NavHeader from '@/components/NavHeader';
import NotificationsPanel from '@/components/NotificationsPanel';
import KeyboardShortcuts from '@/components/KeyboardShortcuts';
import SidePanelHandler from '@/components/SidePanelHandler';

import AdminEditCardModal from '@/admin/AdminEditCardModal';
import AdminEditPostModal from '@/feed/AdminEditPostModal';
import CollectionEditCardModal from '@/collection/CollectionEditCardModal';
import WatchlistAlertsModal from '@/watchlist/WatchlistAlertsModal';
import PendingSalesItemEditModal from '@/admin/PendingSalesItemEditModal';
import AddSaleSideModal from '@/admin/AddSaleSideModal';
import SalesHistoryItemEditModal from '@/admin/SalesHistoryItemEditModal';
import ShowcaseShareModal from '@/showcase/ShowcaseShareModal';

export default {
  components: {
    NavMenu,
    NavHeader,
    NotificationsPanel,
    KeyboardShortcuts,
    SidePanelHandler,
    AdminEditCardModal,
    AdminEditPostModal,
    CollectionEditCardModal,
    WatchlistAlertsModal,
    PendingSalesItemEditModal,
    AddSaleSideModal,
    SalesHistoryItemEditModal,
    ShowcaseShareModal,
  },

  mounted() {
    this.setTheme();
  },

  watch: {
    $route() {
      this.$store.commit('SET_NAV_OPEN', false);
      this.$store.commit('SET_NOTIFICATIONS_OPEN', false);
      this.setTheme();
    },

    theme() {
      this.setTheme();
    },
  },

  computed: {
    ...mapState({
      theme: (state) => state.user.theme,
      navCollapsed: (state) => state.navCollapsed,
      navOpen: (state) => state.navOpen,
      notificationsOpen: (state) => state.notificationsOpen,
    }),

    showNavigation() {
      if (!this.$route.name) {
        return false;
      }

      return !this.$route.meta.preAuth && !this.$route.meta.public && !this.$route.query.mobile;
    },

    showSidePanel() {
      return this.$route.query.cardId
        || this.$route.query.saleId
        || this.$route.query.postId
        || this.$route.query.showcaseCardId
        || this.$route.query.profileId
        || this.$route.query.showSales;
    },
  },

  methods: {
    handleOutsideClick() {
      this.$store.commit('SET_NAV_OPEN', false);
      this.$store.commit('SET_NOTIFICATIONS_OPEN', false);
    },

    setTheme() {
      let { theme } = this;

      if (this.$route.query.theme) {
        theme = this.$route.query.theme;
      } else if (this.$route.meta.public) {
        theme = 'light';
      }

      if (theme === 'dark') {
        document.body.classList.remove('light');
        document.body.classList.add('dark');
      } else {
        document.body.classList.remove('dark');
        document.body.classList.add('light');
      }
    },
  },
};
</script>

<style lang="scss" scoped>
#app {
  min-height: 100%;
  max-width: 100%;
  display: flex;
  flex-direction: column;
  flex: 1;

  --nav-menu-width: 230px;

  @media($medium-up) {
    &.nav-collapsed {
      --nav-menu-width: 105px;
    }
  }
}

main {
  min-height: 100%;
  padding-top: var(--header-height);
  display: flex;
  flex: 1;

  &.auth {
    padding-top: 0;

    #content {
      padding-left: 0;
    }
  }
}

#content {
  display: flex;
  flex-direction: column;
  flex: 1;
  max-width: 100%;
  position: relative;

  @media($medium-up) {
    padding-left: var(--nav-menu-width);
  }
}

.mask {
  position: fixed;
  top: 0;
  right: 0;
  left: 0;
  bottom: 0;
  z-index: 9;
  background-color: var(--theme-backdrop);

  @media($medium-up) {
    display: none;
  }
}
</style>
